export interface FrameContext {
  /** Seconds elapsed since the previous frame, clamped to avoid huge jumps after a tab is backgrounded. */
  deltaSeconds: number;
  /** Seconds elapsed since the loop started. */
  elapsedSeconds: number;
  /** True while Learn Mode is active. `onFrame` still runs and must still render — this only signals that camera rotation/floating motion should pause, not that drawing should stop. */
  frozen: boolean;
}

/**
 * A single requestAnimationFrame driver. `onFrame` always runs (and must
 * always call the renderer's render() itself) — `frozen` is passed through
 * so the callback can pause camera rotation/drift while still rendering
 * up-to-date material/particle state. Skipping the callback entirely while
 * frozen would leave the canvas stuck on a stale frame whenever something
 * else (like a Learn Mode label reveal) changes state during the freeze.
 */
export class AnimationLoop {
  private frozen = false;
  private lastTime = 0;
  private startTime = 0;
  private rafHandle = 0;

  constructor(private readonly onFrame: (ctx: FrameContext) => void) {}

  start(): void {
    this.startTime = performance.now();
    this.lastTime = this.startTime;
    const tick = (now: number) => {
      const deltaSeconds = Math.min((now - this.lastTime) / 1000, 0.1);
      this.lastTime = now;

      this.onFrame({ deltaSeconds, elapsedSeconds: (now - this.startTime) / 1000, frozen: this.frozen });

      this.rafHandle = requestAnimationFrame(tick);
    };
    this.rafHandle = requestAnimationFrame(tick);
  }

  stop(): void {
    cancelAnimationFrame(this.rafHandle);
  }

  setFrozen(frozen: boolean): void {
    this.frozen = frozen;
    this.lastTime = performance.now();
  }

  isFrozen(): boolean {
    return this.frozen;
  }
}
